// Copyright (c) 2024 by Audere

import { Box, Button, List, Stack, Typography } from "@mui/material";
import { ErrorAlert, InfoAlert } from "./Alerts";
import React, { useCallback, useEffect, useState } from "react";
import { createStyleSheet, globalStyles } from "../utils/styles";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import ConversationListItem from "./ConversationListItem";
import SearchBar from "../components/SearchBar";
import { useFilteredConversations } from "../hooks/useFilteredConversations";
import {
  LabelerConversationData,
  QCConversationData,
} from "../features/api/labelApiSlice";
import { SerializedError } from "@reduxjs/toolkit";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { setLabeler } from "../features/slices/labelSlice";
import { useAppDispatch } from "../features/store";

export interface ConversationsListProps {
  isLoading: boolean;
  error: FetchBaseQueryError | SerializedError | undefined;
  conversations: LabelerConversationData[] | QCConversationData[];
  onLoadMore: (value: "next" | "prev") => void;
  hasMore: boolean;
  endOfList: boolean;
}

const ConversationsList: React.FC<ConversationsListProps> = ({
  isLoading,
  error,
  conversations,
  onLoadMore,
  hasMore,
  endOfList,
}) => {
  const { labelTypeID, conversationUID } = useParams();
  const [searchParams] = useSearchParams();
  const offset = searchParams.get("page");
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [searchInput, setSearchInput] = useState("");
  const dispatch = useAppDispatch();

  const handleSearchChange = useCallback((search: string) => {
    setSearchInput(search);
  }, []);

  const basePath = labelTypeID ? `/dashboard/${labelTypeID}` : "";
  const handleConversationClick = useCallback(
    (conversationUID: string, index: number) => {
      if (!labelTypeID) return;

      // Select conversation if none selected or new one is clicked
      if (selectedIndex === -1 || selectedIndex !== index) {
        navigate(`${basePath}/conversation/${conversationUID}?page=${offset}`);
        dispatch(setLabeler(undefined));
        setSelectedIndex(index);
        return;
      }
      // Unselect conversation if the same one is clicked
      navigate(`${basePath}?page=${offset}`);
      setSelectedIndex(-1);
    },
    [
      labelTypeID,
      navigate,
      basePath,
      selectedIndex,
      setSelectedIndex,
      offset,
      dispatch,
    ]
  );

  // Reselect a conversation if it was previously selected before a refresh
  useEffect(() => {
    if (conversationUID) {
      const index = conversations.findIndex(
        (conversation) => conversation.conversation_uid === conversationUID
      );
      setSelectedIndex(index);
    }
  }, [conversationUID, conversations, setSelectedIndex]);

  const filteredConversations = useFilteredConversations(
    conversations,
    searchInput
  );

  // Handle end reached for pagination
  const handlePagination = useCallback(
    (direction: "next" | "prev") => {
      if (!isLoading && hasMore) {
        onLoadMore(direction);
      }
    },
    [isLoading, hasMore, onLoadMore]
  );

  if (!error && (isLoading || conversations.length === 0) && !endOfList) {
    return <InfoAlert message={t("lists.loadingConvos")} />;
  }
  if (error) {
    return <ErrorAlert error={error} />;
  }

  return (
    <Box sx={styles.contentBox}>
      <SearchBar
        onSearchChange={handleSearchChange}
        placeholder="Search by ID, priority, name or email"
        aria-label="Search conversations"
      />
      <Box>
        <Stack direction="row" justifyContent="space-between">
          <Typography sx={styles.subtitleText}>ID / Timestamp</Typography>
          <Typography sx={styles.subtitleText}>Status / Priority</Typography>
        </Stack>
      </Box>
      <Box sx={styles.list}>
        <List aria-label="Conversations list">
          {filteredConversations.map((conversation, index) => {
            const isSelected = selectedIndex === index;

            return (
              <ConversationListItem
                key={conversation.conversation_uid}
                conversation={conversation}
                isSelected={isSelected}
                onClick={() => {
                  handleConversationClick(conversation.conversation_uid, index);
                }}
              />
            );
          })}
          {endOfList && (
            <Box sx={styles.listEndText}>
              <Typography variant="caption" color="textDisabled">
                {t("lists.endOfConvos")}
              </Typography>
            </Box>
          )}
        </List>
      </Box>
      {!searchInput && (
        <Stack direction="row" spacing={2} justifyContent="center" mt={2}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            disabled={Number(offset) === 1}
            onClick={() => {
              handlePagination("prev");
            }}
          >
            {t("lists.previous")}
          </Button>
          <Button
            variant="outlined"
            disabled={endOfList}
            endIcon={<ArrowForwardIcon />}
            onClick={() => {
              handlePagination("next");
            }}
          >
            {t("lists.next")}
          </Button>
        </Stack>
      )}
    </Box>
  );
};

const styles = createStyleSheet({
  contentBox: {
    ...globalStyles.flexColumn,
    height: "100%",
  },
  subtitleText: {
    color: "text.secondary",
    fontWeight: "bold",
  },
  list: {
    border: "1px dashed lightgrey",
    borderRadius: 1.5,
    flex: 1,
    marginTop: 1,
    overflowY: "auto",
    padding: 1,
    height: "100%",
  },
  listEndText: {
    padding: 1,
    textAlign: "center",
    width: "100%",
  },
});

export default ConversationsList;
